import { useEffect, useState } from "react";
import { X, Plus } from "lucide-react";
import type { BrandLockRule, ProductBrand, LockCategory, LockType } from "@/types";
import { colors, semanticColors, radius, spacing, heights, layout, typography, iconSize, iconStroke, shadows, motion } from "@/styles/tokens";
import { DetailPanel } from "@/components/DetailPanel";
import { ReadOnlySave } from "@/components/ReadOnlySave";
import { getTaxonomy } from "@/services/api";

const LABEL: React.CSSProperties = {
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
  fontWeight: typography.fontWeight.medium, marginBottom: spacing[4],
};

const INPUT: React.CSSProperties = {
  width: "100%", padding: `${spacing[6]}px ${spacing[10]}px`, height: heights.input,
  fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface, outline: "none",
  boxSizing: "border-box",
};

const CLIENTS = ["Orinoco", "Kestrel", "Halvorsen", "Brightwater", "Calderon"];
// Brands and categories are master data (configurable identity P1-9): read
// from the taxonomy endpoint when the panel opens, never restated here.
function useTaxonomyNames(): { brands: ProductBrand[]; categories: LockCategory[] } {
  const [names, setNames] = useState<{ brands: ProductBrand[]; categories: LockCategory[] }>({ brands: [], categories: ["All"] });
  useEffect(() => {
    let live = true;
    void getTaxonomy().then((r) => {
      if (live && r.ok) setNames({ brands: r.brands ?? [], categories: [...(r.categories ?? []), "All"] });
    });
    return () => { live = false; };
  }, []);
  return names;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span style={LABEL}>{children}</span>;
}

function TextField({
  label, testid, value, onChange, placeholder,
}: {
  label: string;
  testid: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}</FieldLabel>
      <input
        data-testid={testid}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={INPUT}
      />
    </div>
  );
}

function SelectField<T extends string>({
  label, testid, value, options, onChange,
}: {
  label: string;
  testid: string;
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}</FieldLabel>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        style={INPUT}
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function DateField({
  label, testid, value, onChange, allowEmpty = false,
}: {
  label: string;
  testid: string;
  value: string | null;
  onChange: (v: string | null) => void;
  allowEmpty?: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}{allowEmpty && <span style={{ color: semanticColors.textPlaceholder, textTransform: "none", marginLeft: spacing[6] }}>(optional)</span>}</FieldLabel>
      <input
        data-testid={testid}
        type="date"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
        style={INPUT}
      />
    </div>
  );
}

function LockTypeRadio({
  value, onChange,
}: {
  value: LockType;
  onChange: (v: LockType) => void;
}) {
  const opts: LockType[] = ["Strict", "Preferred"];
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>Lock Type</FieldLabel>
      <div className="flex items-center" style={{ gap: spacing[16], height: heights.input }}>
        {opts.map((o) => (
          <label
            key={o}
            data-testid={`radio-lock-${o.toLowerCase()}`}
            className="flex items-center"
            style={{ gap: spacing[6], fontSize: typography.fontSize.base, color: semanticColors.textPrimary, cursor: "pointer" }}
          >
            <input
              type="radio"
              name="lockType"
              checked={value === o}
              onChange={() => onChange(o)}
              style={{ accentColor: colors.indigo600 }}
            />
            {o}
          </label>
        ))}
      </div>
    </div>
  );
}

function AllowedBrandsField({
  value, onChange, allBrands,
}: {
  value: ProductBrand[];
  onChange: (v: ProductBrand[]) => void;
  allBrands: ProductBrand[];
}) {
  const remaining = allBrands.filter((b) => !value.includes(b));
  const remove = (b: ProductBrand) => onChange(value.filter((x) => x !== b));
  const add    = (b: ProductBrand) => onChange([...value, b]);
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>Allowed Brands</FieldLabel>
      <div className="flex items-center" style={{ flexWrap: "wrap", gap: spacing[6], minHeight: heights.input }}>
        {value.length === 0 && (
          <span style={{ fontSize: typography.fontSize.sm, color: semanticColors.textPlaceholder }}>No brands selected</span>
        )}
        {value.map((b) => (
          <span
            key={b}
            data-testid={`brand-chip-${b}`}
            className="flex items-center"
            style={{
              gap: spacing[4], height: heights.iconButton, padding: `0 ${spacing[4]}px 0 ${spacing[10]}px`,
              backgroundColor: colors.indigo50, color: semanticColors.accentTextOnTint,
              border: `1px solid ${colors.indigo100}`, borderRadius: radius.base,
              fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium,
            }}
          >
            {b}
            <button
              data-testid={`brand-chip-remove-${b}`}
              onClick={() => remove(b)}
              title={`Remove ${b}`}
              style={{
                width: heights.icon, height: heights.icon, padding: 0, cursor: "pointer",
                background: "transparent", border: "none", color: semanticColors.accentTextOnTint,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <X size={iconSize.sm} strokeWidth={iconStroke.bold} />
            </button>
          </span>
        ))}
      </div>
      {remaining.length > 0 && (
        <div className="flex items-center" style={{ flexWrap: "wrap", gap: spacing[6], marginTop: spacing[8] }}>
          <span style={{ fontSize: typography.fontSize.xs, color: semanticColors.textPlaceholder, textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide }}>
            Add
          </span>
          {remaining.map((b) => (
            <button
              key={b}
              data-testid={`brand-chip-add-${b}`}
              onClick={() => add(b)}
              className="flex items-center"
              style={{
                gap: spacing[4], height: heights.chip, padding: `0 ${spacing[8]}px`,
                backgroundColor: colors.white, color: semanticColors.textSecondary,
                border: `1px dashed ${semanticColors.textDisabled}`, borderRadius: radius.base,
                fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium, cursor: "pointer",
              }}
            >
              <Plus size={iconSize.xs} strokeWidth={iconStroke.regular} />
              {b}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ActiveToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between">
      <FieldLabel>Active</FieldLabel>
      <button
        data-testid="rule-active-toggle"
        onClick={() => onChange(!value)}
        style={{
          width: layout.toggleTrackWidth, height: heights.pill, padding: spacing[2], borderRadius: radius.full,
          border: "none", cursor: "pointer",
          backgroundColor: value ? colors.indigo600 : semanticColors.textDisabled,
          display: "flex", alignItems: "center",
          justifyContent: value ? "flex-end" : "flex-start",
          transition: `background-color ${motion.fast}`,
        }}
      >
        <span style={{
          width: heights.icon, height: heights.icon, borderRadius: "50%",
          backgroundColor: colors.white,
          boxShadow: shadows.toggleKnob,
        }} />
      </button>
    </div>
  );
}

export function BrandLockDetailPanel({
  rule,
  onClose,
}: {
  rule: BrandLockRule | null;
  onClose: () => void;
}) {
  const taxonomy = useTaxonomyNames();
  const [form, setForm] = useState<BrandLockRule | null>(rule);

  useEffect(() => { setForm(rule); }, [rule]);

  if (!rule || !form) return null;

  const setField = <K extends keyof BrandLockRule>(k: K, v: BrandLockRule[K]) => {
    setForm((cur) => (cur ? { ...cur, [k]: v } : cur));
  };

  return (
    <DetailPanel
      testIdPrefix="rule"
      title={`${form.endClient} — ${form.category}`}
      subtitle={form.id}
      onClose={onClose}
      footer={
        <>
          <button
            data-testid="rule-panel-cancel"
            className="btn-secondary"
            onClick={onClose}
            style={{
              height: heights.control, padding: `0 ${spacing[14]}px`,
              backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
              border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
              fontSize: typography.fontSize.base, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <ReadOnlySave testId="rule-panel-save" />
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: spacing[14] }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14] }}>
          <SelectField label="End Client" testid="rf-client"
                       value={form.endClient}
                       options={CLIENTS}
                       onChange={(v) => setField("endClient", v)} />
          <SelectField label="Category" testid="rf-category"
                       value={form.category}
                       options={taxonomy.categories}
                       onChange={(v) => setField("category", v)} />
        </div>

        <LockTypeRadio value={form.lockType} onChange={(v) => setField("lockType", v)} />

        <AllowedBrandsField value={form.allowedBrands} onChange={(v) => setField("allowedBrands", v)}
                            allBrands={taxonomy.brands} />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14] }}>
          <DateField label="Valid From" testid="rf-valid-from"
                     value={form.validFrom}
                     onChange={(v) => setField("validFrom", v ?? "")} />
          <DateField label="Valid To" testid="rf-valid-to"
                     value={form.validTo}
                     onChange={(v) => setField("validTo", v)}
                     allowEmpty />
        </div>

        <TextField label="Source Note" testid="rf-source-note"
                   value={form.sourceNote}
                   onChange={(v) => setField("sourceNote", v)}
                   placeholder="e.g. Orinoco global agreement 2024" />

        <ActiveToggle value={form.active} onChange={(v) => setField("active", v)} />
      </div>
    </DetailPanel>
  );
}
